/*
  Data transforms for the prediction dial — ported from the mobile app
  (src/screens/Profile/components/PredictionsSection/utils.ts) so the web
  wheel renders the same segments, colors and percentages.
*/

// Segment colors cycle by index; adjacent segments never share a color
export const SEGMENT_COLORS = ['#f4f0f0', '#acd1ff'];

// Hybrid percentage config: below VOTE_THRESHOLD total votes, blend actual
// distribution with an equal split so early segments stay readable/tappable
export const VOTE_THRESHOLD = 10;
export const MIN_SEGMENT_PERCENTAGE = 8;
export const BLEND_FACTOR = 0.3; // 30% actual + 70% equal below threshold

export function getScoreLabel(prediction) {
  if (prediction?.predictedScore) {
    return `${prediction.predictedScore.home}-${prediction.predictedScore.away}`;
  }
  return prediction?.outcome || '';
}

export function getSegmentColors(totalSegments) {
  if (totalSegments === 0) return [];
  if (totalSegments === 1) return [SEGMENT_COLORS[0]];

  const numColors = SEGMENT_COLORS.length;
  const result = [];

  for (let i = 0; i < totalSegments; i++) {
    const prevColor = i > 0 ? result[i - 1] : null;
    const isLastSegment = i === totalSegments - 1;
    const firstColor = result[0];

    const preferredIndex = i % numColors;
    const preferredColor = SEGMENT_COLORS[preferredIndex];

    const conflictsWithPrev = preferredColor === prevColor;
    const conflictsWithFirst = isLastSegment && preferredColor === firstColor;

    if (!conflictsWithPrev && !conflictsWithFirst) {
      result.push(preferredColor);
    } else {
      for (let offset = 1; offset < numColors; offset++) {
        const altIndex = (preferredIndex + offset) % numColors;
        const altColor = SEGMENT_COLORS[altIndex];
        const altConflictsPrev = altColor === prevColor;
        const altConflictsFirst = isLastSegment && altColor === firstColor;

        if (!altConflictsPrev && !altConflictsFirst) {
          result.push(altColor);
          break;
        }
      }
    }
  }

  return result;
}

export function calculateHybridPercentages(predictions) {
  const numSegments = predictions.length;
  if (numSegments === 0) return [];

  const totalVotes = predictions.reduce((sum, p) => sum + (p.voteCount || 0), 0);
  const equalPercentage = 100 / numSegments;

  let percentages;

  if (totalVotes === 0) {
    percentages = predictions.map(() => equalPercentage);
  } else if (totalVotes < VOTE_THRESHOLD) {
    percentages = predictions.map((p) => {
      const actualPct = ((p.voteCount || 0) / totalVotes) * 100;
      return actualPct * BLEND_FACTOR + equalPercentage * (1 - BLEND_FACTOR);
    });
  } else {
    percentages = predictions.map((p) => ((p.voteCount || 0) / totalVotes) * 100);
  }

  const belowMinCount = percentages.filter((p) => p < MIN_SEGMENT_PERCENTAGE).length;
  if (belowMinCount > 0 && belowMinCount < numSegments) {
    const deficit = percentages.reduce(
      (sum, p) => sum + (p < MIN_SEGMENT_PERCENTAGE ? MIN_SEGMENT_PERCENTAGE - p : 0),
      0
    );

    const aboveMinSegments = percentages.filter((p) => p >= MIN_SEGMENT_PERCENTAGE);
    const totalAboveMin = aboveMinSegments.reduce((sum, p) => sum + p, 0);

    percentages = percentages.map((p) => {
      if (p < MIN_SEGMENT_PERCENTAGE) return MIN_SEGMENT_PERCENTAGE;
      return p - (p / totalAboveMin) * deficit;
    });
  }

  const total = percentages.reduce((sum, p) => sum + p, 0);
  if (total !== 100) {
    percentages = percentages.map((p) => (p / total) * 100);
  }

  return percentages;
}

export function calculateActualPercentage(prediction, predictions) {
  const totalVotes = predictions.reduce((sum, p) => sum + (p.voteCount || 0), 0);
  if (totalVotes === 0) return 0;
  return ((prediction.voteCount || 0) / totalVotes) * 100;
}

export function transformPredictionsToSegments(predictions) {
  if (!predictions?.length) return [];

  const displayPercentages = calculateHybridPercentages(predictions);
  const segmentColors = getSegmentColors(predictions.length);

  return predictions.map((prediction, index) => ({
    id: prediction._id,
    percentage: displayPercentages[index],
    actualPercentage: calculateActualPercentage(prediction, predictions),
    color: segmentColors[index],
    label: getScoreLabel(prediction)
  }));
}

export function findUserVotePrediction(predictions) {
  return predictions?.find((p) => p.isUserVote) || null;
}

// --- color helpers (segment shading + label contrast) ---

export function hexToRgb(hex) {
  const normalized = hex.replace('#', '').trim();

  if (normalized.length === 3) {
    return {
      r: parseInt(normalized[0] + normalized[0], 16),
      g: parseInt(normalized[1] + normalized[1], 16),
      b: parseInt(normalized[2] + normalized[2], 16)
    };
  }

  if (normalized.length === 6) {
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16)
    };
  }

  return null;
}

function rgbToHex(r, g, b) {
  const toHex = (n) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return '#' + toHex(r) + toHex(g) + toHex(b);
}

export function lightenColor(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const { r, g, b } = rgb;
  return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
}

export function darkenColor(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const { r, g, b } = rgb;
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

// Light backgrounds get black labels, dark backgrounds get white
export function isLightColor(color) {
  if (!color) return false;
  const rgb = color.trim()[0] === '#' ? hexToRgb(color.trim()) : null;
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance >= 0.5;
}

export function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  };
}
